/**
 * authStripeHandlers
 *
 * Stripe checkout integration handlers for AuthContext.
 * Manages checkout flow initiation and finalization.
 */
import { auth } from '../../firebase/firebase';
import { 
  storeStripeCheckoutState, 
  getStripeCheckoutState, 
  clearStripeCheckoutState,
  getAuthBackup 
} from './authSessionUtils';

/**
 * Initialize Stripe checkout flow
 * Stores necessary state for auth restoration after Stripe redirect
 * 
 * @param {string} sessionId - Stripe checkout session ID
 * @param {Object|null} user - Current user object from context
 * @param {Object|null} directFirebaseUser - Direct Firebase user reference
 * @returns {boolean} - Whether initialization was successful
 */
export const initiateStripeCheckout = (sessionId, user, directFirebaseUser) => {
  // Check for user from multiple sources
  const effectiveUser = user || directFirebaseUser || auth.currentUser;
  
  // Also check for auth backup data as fallback
  const authBackup = !effectiveUser ? getAuthBackup() : null;
  const userUid = effectiveUser?.uid || authBackup?.uid;
  
  console.log('initiateStripeCheckout debug:', {
    hasUser: !!user,
    hasDirectFirebaseUser: !!directFirebaseUser,
    hasAuthCurrentUser: !!auth.currentUser,
    hasEffectiveUser: !!effectiveUser,
    hasAuthBackup: !!authBackup,
    userUid
  });
  
  if (!userUid) {
    console.error('Cannot initiate Stripe checkout without a user ID');
    return false;
  }

  storeStripeCheckoutState(sessionId, userUid);
  return true;
};

/**
 * Finalize Stripe checkout flow
 * Clears stored state and returns checkout result
 * 
 * @param {boolean} success - Whether checkout was successful
 * @param {string|null} sessionId - Stripe session ID
 * @returns {Object} - Checkout result with success, sessionId, and returnPath
 */
export const finalizeStripeCheckout = (success = false, sessionId = null) => {
  const checkoutData = getStripeCheckoutState();
  const returnPath = checkoutData?.returnPath || '/pricing';
  
  clearStripeCheckoutState();
  
  return {
    success,
    sessionId,
    returnPath
  };
};

/**
 * Check if returning from Stripe checkout
 * 
 * @param {Object} firebaseUser - Firebase user object
 * @returns {boolean} - Whether user is returning from Stripe
 */
export const isReturningFromStripeCheckout = (firebaseUser) => {
  const checkoutData = getStripeCheckoutState();
  
  if (checkoutData && firebaseUser) {
    return checkoutData.inProgress && checkoutData.uid === firebaseUser.uid;
  }
  
  return false;
};

/**
 * Handle Stripe checkout auth state restoration
 * Called when user returns from Stripe checkout
 * 
 * @param {Object} firebaseUser - Firebase user object
 * @param {string} stripeAuthState - Stored auth state from before redirect
 * @returns {boolean} - Whether auth state matches
 */
export const validateStripeAuthState = (firebaseUser, stripeAuthState) => {
  if (!stripeAuthState || !firebaseUser) {
    return false;
  }
  
  try {
    const storedAuth = JSON.parse(stripeAuthState);
    if (storedAuth.uid === firebaseUser.uid) {
      console.log('authStripeHandlers: Auth state matches stored state from Stripe checkout');
      return true;
    }
  } catch (e) {
    console.error('authStripeHandlers: Error parsing stored auth state:', e);
  }
  
  return false;
};

